import { Component, effect, inject, output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PopulateIDAuthUserLoginRequest } from '@api/model/populateIDAuthUserLoginRequest';
import { AuthApiService } from '@api/service/auth.api.service';
import { VoltaAuthService } from '@core/auth/volta-auth.service';
import { UserService } from '@core/client/user.service';
import { ErrorService } from '@core/common/error.service';
import { InvalidLoginError, RequiresTwoFactorError, UserExceededMaximumSessionsError } from '@core/common/errors';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormDisplayControl } from '@oi-soft/common';
import { FormComponent } from "@shared/components/form/form.component";
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';
import { GlobalModalsService } from '@shared/services/global-modals.service';
import { VoltaSweetAlertService } from '@shared/services/volta-sweet-alert/volta-sweet-alert.service';
import { firstValueFrom, tap } from 'rxjs';
import { FormControlComponent } from "../../../../shared/components/form/form-control/form-control.component";
import { ModalFormButtonGroupComponent } from "../../../../shared/components/modals/modal/modal-button-group/modal-form-button-group/modal-form-button-group.component";
import { LoginTFAModalComponent } from "../login-tfa-modal/login-tfa-modal.component";

@Component({
    selector: 'app-login-form',
    imports: [ReactiveFormsModule, FormControlComponent, FormControlComponent, LoginTFAModalComponent, ModalFormButtonGroupComponent, FontAwesomeModule, TooltipDirective, FormComponent],
    templateUrl: './login-form.component.html',
    styleUrl: './login-form.component.scss'
})
export class LoginFormComponent {
    
    private authService = inject(VoltaAuthService);
    private errorService = inject(ErrorService);
    private globalModalsService = inject(GlobalModalsService);
    private authApiService = inject(AuthApiService);
    private userService = inject(UserService);
    private swalService = inject(VoltaSweetAlertService);
    
    login = output<boolean>();
    
    showTFA = signal<boolean>(false);
    
    form = new FormGroup({
        usernameOrEmail: new FormDisplayControl(
            'Email or Username', '',
            {
                validators: [Validators.required],
            }
        ),
        password: new FormDisplayControl(
            'Password', '',
            {
                validators: [Validators.required]
            }
        ),
        remember: new FormDisplayControl(
            'Remember Me', false
        )
    });

    constructor(){

        effect(() => {
            if(this.globalModalsService.sessionsModal()){

                this.globalModalsService.sessionsModal()?.sessionsBelowLimit.subscribe((response) => {
                    if(response && this.authService.isAuthorised()){
                        this.finaliseLogin();
                    }
                })
                
            }
        })

    }
    
    onSubmitLogin(){
        this.loginUser({
            usernameOrEmail: this.form.controls.usernameOrEmail.value,
            password: this.form.value.password
        });
    }
    
    onSubmitLoginTFA(details:{token?:string, recovery?:string}){
        this.loginUser({
            usernameOrEmail: this.form.controls.usernameOrEmail.value,
            password: this.form.value.password,
            twoFactorCode: details.token,
            twoFactorRecoveryCode: details.recovery
        });
    }
    
    loginUser(request:PopulateIDAuthUserLoginRequest){

        this.swalService.fireLoading({message: "Logging you in, please wait."});

        firstValueFrom(this.identityLogin(request))
        .then((authorisationResponse) => {

            this.showTFA.set(false);
            this.swalService.close();

            //Create Authorisation Cookie
            this.authService.authoriseAccessToken(
                authorisationResponse.accessToken,
                authorisationResponse.refreshToken,
                authorisationResponse.expiresIn,
                this.form.value.remember
            );

            //Manage Sessions and Finalise Login
            this.finaliseLogin();

        })
        .catch((error) => {
            //if response asked for TFA, show TFA modal
            if(error instanceof RequiresTwoFactorError) {
                this.showTFA.set(true);
            }
            else if(error instanceof InvalidLoginError){
                
                if(request.twoFactorCode || request.twoFactorRecoveryCode){
                    error.name = "Invalid TFA";
                    error.message = "The TFA you've inserted is incorrect.";
                }

                this.swalService.fireError(error);
                
            }
            else{
                this.swalService.fireError(error);
            }
        })
        
        
    }

    identityLogin(request:PopulateIDAuthUserLoginRequest){
        return this.authApiService.Auth.Login.Post({
            body: request
        })
        .pipe(tap(this.swalService.closeLoadingPreSubscribe()));
    }

    finaliseLogin(){

        this.swalService.fireLoading({message: "Logging you in, please wait."});

        //Populate User Details in User Service
        firstValueFrom(this.userService.getSessionUser())
        .then(() => {
            this.swalService.fireSuccess({message: "You've Logged in.", timer: true});
            this.form.reset();
            this.login.emit(true);
        })
        .catch((error) => {

            //If Sessions exceeded, open session modal
            if(error instanceof UserExceededMaximumSessionsError){
                // Interceptor handles it
                this.swalService.close();
            }
            //Else 
            else{
                this.swalService.fireError(error);
                firstValueFrom(this.userService.userLogOut());
            }
        })

    }
    
}
